import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { getCurrentMedicationSettings, getCurrentStateTerritoryTermSettings } from '../Async/MedicationSettingsAsync';
import { useError } from '../Components/ErrorBoundary';
import { ApplicationStorageKeys, loadFromLocalStorage, saveToLocalStorage } from '../Shared/LocalStorage';
import { Nullable } from '../Shared/Types';

export interface MedicationSettings {
    genericPrescribingOnly: boolean;
    alwaysCreatePharmacyRequest: boolean;
    pharmacyRequestExpiryRequired: boolean;
    providersAreLinkedToUserAccounts: boolean;
    allowFreeTextMedication: boolean;
    enabledEditingExpiryDateAfterFinalised: boolean;
    defaultPharmacyRequestLength: Nullable<TimeLength>;
    maximumPharmacyRequestLength: Nullable<TimeLength>;
    reloginChallengeGracePeriod: Nullable<TimeLength>;
    scriptExchangeTimeout: Nullable<TimeLength>;
    encounterPlaceTerm: Term;
    pharmacyRequestTerm: Term;
    authorityNumberTerm: Term;
    approvalNumberTerm: Term;
    decisionSupportSettings: DecisionSupportSettings;
    sendScriptRequestsToScriptExchange: boolean;
    sendPharmacyRequestsToScriptExchange: boolean;
    hideViewingRights: boolean;
    hideProblems: boolean;
    verbalOrderTerm: Term;
    enableFreeTextWitness: boolean;
    enableSupportiveDocumentation: boolean;
    modalMedications: boolean;
    defaultS8Duration: Nullable<TimeLength>;
    defaultShortCourseDuration: Nullable<TimeLength>;
    requestEmail: string;
    defaultMedicationDuration: Nullable<TimeLength>;
}

interface DecisionSupportSettings {
    prescriptionMonitoringEnabled: boolean;
    medicationToMedicationEnabled: boolean;
    medicationToAllergiesEnabled: boolean;
    medicationToPregnancyEnabled: boolean;
    medicationToConditionEnabled: boolean;
    showDecisionSupportWarning: boolean;
    decisionSupportWarningTitle: string;
    decisionSupportWarningDescription: string;
    decisionSupportWarningDisclaimer: string;
}

export interface StateTerritoryTermSetting {
    stateCode: string;
    jurisdictionalControlledSubstanceReferenceTerm: Term;
}

export enum TimeUnits {
    Days = 'days',
    Months = 'months',
    Hours = 'hours',
    Minutes = 'minutes',
    Seconds = 'seconds'
}

export function convertTimeUnitsToUnitOfTime(timeUnit: TimeUnits): moment.unitOfTime.Diff {
    switch (timeUnit) {
        case TimeUnits.Days:
            return 'days';
        case TimeUnits.Months:
            return 'months';
        case TimeUnits.Hours:
            return 'hours';
        case TimeUnits.Minutes:
            return 'minutes';
        case TimeUnits.Seconds:
            return 'seconds';
    }

    return 'minutes';
}

export function convertTimeOutToMilliSeconds(number: number, timeUnit: TimeUnits): number {
    switch (timeUnit) {
        case TimeUnits.Months:
            return Math.floor(number * 365 * 24 * 60 * 60 * 1000);
        case TimeUnits.Days:
            return Math.floor(number * 24 * 60 * 60 * 1000);
        case TimeUnits.Hours:
            return Math.floor(number * 60 * 60 * 1000);
        case TimeUnits.Minutes:
            return Math.floor(number * 60 * 1000);
        case TimeUnits.Seconds:
            return Math.floor(number * 1000);
        default:
            return 1500; //default timeout 15 milliseconds
    }
}

export interface TimeLength {
    number: number;
    timeUnits: TimeUnits;
}

export interface Term {
    abbreviatedPluralTerm: string;
    abbreviatedSingularTerm: string;
    pluralTerm: string;
    singularTerm: string;
}

let medicationSettings: MedicationSettings;
let cachedStateTerritoryTermSettings: StateTerritoryTermSetting[];

export async function getOnDemandMedicationSettings(): Promise<MedicationSettings> {
    if (!medicationSettings) {
        try {
            const newMedicationSettings = await getCurrentMedicationSettings();
            setMedicationSettings(newMedicationSettings);
            return newMedicationSettings;
        } catch (err) {
            throw Error('Unable to load medication settings');
        }
    }

    return medicationSettings;
}

async function getOnDemandStateTerritoryTermSettings(): Promise<StateTerritoryTermSetting[]> {
    if (!cachedStateTerritoryTermSettings) {
        try {
            const newStateTerritoryTermSettings = await getCurrentStateTerritoryTermSettings();
            cacheStateTerritoryTermSettings(newStateTerritoryTermSettings);
            return newStateTerritoryTermSettings;
        } catch (err) {
            throw Error('Unable to load state territory settings');
        }
    }
    return cachedStateTerritoryTermSettings;
}

export enum SettingsStatus {
    Init,
    Loading,
    Loaded
}

export type MedicationSettingsType = {
    status: SettingsStatus;
    settings: MedicationSettings;
    getTermFromState: (stateCode?: Nullable<string>) => string;
    getDefaultDurationAsMoment: () => moment.Moment;
    getS8DurationAsMoment: () => moment.Moment | null;
    getShortCourseDurationAsMoment: () => moment.Moment | null;
};

export function useMedicationSettings(): MedicationSettingsType {
    const showError = useError();
    const [status, setStatus] = useState(SettingsStatus.Init);
    const [settings, setSettings] = useState<MedicationSettings>(
        loadFromLocalStorage(ApplicationStorageKeys.MedicationSettings, {
            genericPrescribingOnly: false,
            alwaysCreatePharmacyRequest: false,
            pharmacyRequestExpiryRequired: false,
            providersAreLinkedToUserAccounts: true,
            allowFreeTextMedication: false,
            enabledEditingExpiryDateAfterFinalised: false,
            defaultPharmacyRequestLength: {
                number: 6,
                timeUnits: TimeUnits.Months
            },
            reloginChallengeGracePeriod: {
                number: 0,
                timeUnits: TimeUnits.Minutes
            },
            maximumPharmacyRequestLength: {
                number: 6,
                timeUnits: TimeUnits.Months
            },
            scriptExchangeTimeout: {
                number: 15,
                timeUnits: TimeUnits.Seconds
            },
            encounterPlaceTerm: {
                abbreviatedPluralTerm: 'Places',
                abbreviatedSingularTerm: 'Place',
                pluralTerm: 'Encounter places',
                singularTerm: 'Encounter place'
            },
            pharmacyRequestTerm: {
                abbreviatedPluralTerm: 'Requests',
                abbreviatedSingularTerm: 'Request',
                pluralTerm: 'Pharmacy requests',
                singularTerm: 'Pharmacy request'
            },
            approvalNumberTerm: {
                abbreviatedPluralTerm: 'Authority approval numbers',
                abbreviatedSingularTerm: 'Authority approval number',
                pluralTerm: 'Authority approval numbers',
                singularTerm: 'Authority approval number'
            },
            authorityNumberTerm: {
                abbreviatedPluralTerm: 'Authority prescription numbers',
                abbreviatedSingularTerm: 'Authority prescription number',
                pluralTerm: 'Authority prescription numbers',
                singularTerm: 'Authority prescription number'
            },
            decisionSupportSettings: {
                prescriptionMonitoringEnabled: true,
                medicationToAllergiesEnabled: false,
                medicationToConditionEnabled: false,
                medicationToMedicationEnabled: false,
                medicationToPregnancyEnabled: false,
                decisionSupportWarningDescription: '',
                decisionSupportWarningTitle: '',
                showDecisionSupportWarning: false,
                decisionSupportWarningDisclaimer: ''
            },
            sendScriptRequestsToScriptExchange: true,
            sendPharmacyRequestsToScriptExchange: false,
            hideViewingRights: false,
            hideProblems: false,
            verbalOrderTerm: {
                abbreviatedPluralTerm: 'VOs',
                abbreviatedSingularTerm: 'VO',
                pluralTerm: 'Verbal orders',
                singularTerm: 'Verbal order'
            },
            enableFreeTextWitness: false,
            enableSupportiveDocumentation: false,
            modalMedications: false,
            defaultS8Duration: {
                number: 0,
                timeUnits: TimeUnits.Days
            },
            defaultShortCourseDuration: {
                number: 0,
                timeUnits: TimeUnits.Days
            },
            requestEmail: 'editor@medicaldirector.com',
            defaultMedicationDuration: {
                number: 5,
                timeUnits: TimeUnits.Days
            }
        })
    );
    const [stateTerritoryTermSettings, setStateTerritoryTermSettings] = useState<StateTerritoryTermSetting[]>(
        loadFromLocalStorage(ApplicationStorageKeys.StateTerritoryTermSettings, [])
    );

    const loadSettings = async () => {
        setStatus(SettingsStatus.Loading);
        try {
            const newSettings = await getOnDemandMedicationSettings();
            setSettings(newSettings);
            saveToLocalStorage(ApplicationStorageKeys.MedicationSettings, newSettings);

            const newStateTerritoryTermSettings = await getOnDemandStateTerritoryTermSettings();
            setStateTerritoryTermSettings(newStateTerritoryTermSettings);
            saveToLocalStorage(ApplicationStorageKeys.StateTerritoryTermSettings, newStateTerritoryTermSettings);

            setStatus(SettingsStatus.Loaded);
        } catch (err) {
            showError(err);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const getTermFromState = (stateCode?: Nullable<string>) => {
        const defaultTerm = 'Controlled substance reference';

        if (!stateCode) return defaultTerm;

        for (const index in stateTerritoryTermSettings) {
            const stateTerm = stateTerritoryTermSettings[index];
            if (stateTerm.stateCode.toLowerCase() === stateCode?.toLowerCase()) {
                return stateTerm.jurisdictionalControlledSubstanceReferenceTerm.singularTerm;
            }
        }

        return defaultTerm;
    };

    const getDefaultDurationAsMoment = (): moment.Moment => {
        return moment().add(
            settings.defaultPharmacyRequestLength?.number ?? 6,
            settings.defaultPharmacyRequestLength?.timeUnits ?? TimeUnits.Months
        );
    };

    const getS8DurationAsMoment = () => {
        return settings.defaultS8Duration?.number !== null
            ? moment().add(settings.defaultS8Duration?.number, settings.defaultS8Duration?.timeUnits ?? TimeUnits.Days)
            : null;
    };

    const getShortCourseDurationAsMoment = () => {
        return settings.defaultShortCourseDuration?.number !== null
            ? moment().add(
                  settings.defaultShortCourseDuration?.number,
                  settings.defaultShortCourseDuration?.timeUnits ?? TimeUnits.Days
              )
            : null;
    };
    return {
        status,
        settings,
        getTermFromState,
        getDefaultDurationAsMoment,
        getS8DurationAsMoment,
        getShortCourseDurationAsMoment
    };
}

export function setMedicationSettings(newMedicationSettings: MedicationSettings): void {
    medicationSettings = newMedicationSettings;
}

function cacheStateTerritoryTermSettings(newStateTerritoryTermSettings: StateTerritoryTermSetting[]): void {
    cachedStateTerritoryTermSettings = newStateTerritoryTermSettings;
}
